import React, { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LayoutProvider } from "@/hooks/useLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { setWorkspaceId, getWorkspaceId } from "@/hooks/useWorkspaceId";
import { InsightFlareProvider } from "@/contexts/InsightFlareContext";
import { InsightFlareIdentity } from "@/components/InsightFlareIdentity";
import { DialogsProvider } from "@/components/dialogs/DialogsProvider";
import Auth from "@/pages/auth";
import AuthCallback from "@/pages/auth/AuthCallback";
import CliCallback from "@/pages/auth/CliCallback";
import CliDeviceAuth from "@/pages/auth/CliDeviceAuth";
import NotFound from "@/pages/NotFound";
import PublicSharePage from "@/pages/PublicSharePage";
import PublicFrame from "@/pages/PublicFrame";
import { SyncEngineProvider } from "@/components/providers/SyncEngineProvider";
import { PageSkeleton } from "@/components/PageSkeleton";

// ── Top-level lazy-loaded pages (NEVER create inside JSX) ────────────────────
const Dashboard      = lazy(() => import("./pages/Dashboard"));
const StoragePage    = lazy(() => import("./pages/StoragePage"));
const SettingsPage   = lazy(() => import("./pages/SettingsPage"));
const FunctionsPage  = lazy(() => import("./pages/FunctionsPage"));
const PagesPage      = lazy(() => import("./pages/PagesPage"));
const DatabasePage   = lazy(() => import("./pages/DatabasePage"));
const BuilderPage    = lazy(() => import("./pages/builder/BuilderPage"));
const HostingPage    = lazy(() => import("./pages/HostingPage"));
const MarketplacePage= lazy(() => import("./pages/MarketplacePage"));
const HostingDetail  = lazy(() => import("./pages/HostingDetail"));
const SecretsPage    = lazy(() => import("./pages/SecretsPage"));
const EmailPage      = lazy(() => import("./pages/email/EmailPage"));

const BuilderWorkspace = lazy(() => import("./pages/builder/BuilderWorkspace"));
const CheckoutPage   = lazy(() => import("@/components/settings/CheckoutPage").then(m => ({ default: m.CheckoutPage })));
const PolarCheckoutCallback = lazy(() => import("@/components/settings/PolarCheckoutCallback").then(m => ({ default: m.PolarCheckoutCallback })));

const queryClient = new QueryClient();

const RouteFallback = PageSkeleton;

// Restore saved accent color on app init
(function restoreAccentColor() {
  const hex = localStorage.getItem('genofx:accent-color');
  if (hex && /^#[0-9a-f]{6}$/i.test(hex)) {
    const r = parseInt(hex.slice(1,3),16)/255;
    const g = parseInt(hex.slice(3,5),16)/255;
    const b = parseInt(hex.slice(5,7),16)/255;
    const max=Math.max(r,g,b),min=Math.min(r,g,b);
    let h=0,s=0;
    const l=(max+min)/2;
    if(max!==min){const d=max-min;s=l>0.5?d/(2-max-min):d/(max+min);switch(max){case r:h=((g-b)/d+(g<b?6:0))/6;break;case g:h=((b-r)/d+2)/6;break;case b:h=((r-g)/d+4)/6;break;}}
    const hsl=`${Math.round(h*360)} ${Math.round(s*100)}% ${Math.round(l*100)}%`;
    ['--primary','--accent','--glow-purple','--ring','--sidebar-primary','--sidebar-ring'].forEach(v=>document.documentElement.style.setProperty(v,hsl));
  }
})();

const App = () => {
  useEffect(() => {
    if (!getWorkspaceId()) setWorkspaceId('1');
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LayoutProvider>
            <TooltipProvider>
              <SyncEngineProvider>
                <InsightFlareProvider>
                  <InsightFlareIdentity />
                  <DialogsProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      {/* Auth */}
                      <Route path="/auth">
                        <Route index element={<Navigate to="/auth/login" replace />} />
                        <Route path="signup"   element={<Auth mode="signup" />} />
                        <Route path="login"    element={<Auth mode="login" />} />
                        <Route path="cli"      element={<CliDeviceAuth />} />
                        <Route path="cli/callback" element={<CliCallback />} />
                        <Route path="reset"    element={<Auth mode="reset" />} />
                        <Route path="verify"   element={<Auth mode="verify" />} />
                        <Route path="callback" element={<AuthCallback />} />
                      </Route>

                      {/* Public */}
                      <Route path="/share/:shareId"              element={<PublicSharePage />} />
                      <Route path="/public/:workspaceId/:frameId" element={<PublicFrame />} />

                      {/* Invite accept page */}
                      <Route path="/invite" element={<Navigate to="/" replace />} />

                      {/* Checkout (also inside main Suspense) */}
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/checkout/callback" element={<PolarCheckoutCallback />} />

                      {/* Main app */}
                      <Route element={<AppLayout />}>
                        <Route path="/"          element={<BuilderPage />} />
                        <Route path="/analytics" element={<Dashboard />} />
                        <Route path="/hosting"   element={<HostingPage />} />
                        <Route path="/marketplace" element={<MarketplacePage />} />
                        <Route path="/hosting/:id" element={<HostingDetail />} />
                        <Route path="/functions" element={<FunctionsPage />} />
                        <Route path="/pages"     element={<PagesPage />} />
                        <Route path="/database"  element={<DatabasePage />} />
                        <Route path="/database/:dbName"  element={<DatabasePage />} />
                        <Route path="/database/:dbName/:collection"  element={<DatabasePage />} />
                        <Route path="/files"     element={<StoragePage />} />
                        <Route path="/email"     element={<EmailPage />} />
                        <Route path="/secrets"   element={<SecretsPage />} />
                        <Route path="/settings"  element={<SettingsPage />} />
                        <Route path="/profile"   element={<SettingsPage />} />
                        <Route path="*"          element={<NotFound />} />
                      </Route>

                      {/* Builder workspace — full page, no sidebar */}
                      <Route path="/builder/:projectId" element={<BuilderWorkspace />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
                  </DialogsProvider>
                </InsightFlareProvider>
              </SyncEngineProvider>
            </TooltipProvider>
          </LayoutProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
